import { useUpdateAtom } from "jotai/utils";
import React, { useEffect, useRef } from "react";
import urlRegex from "url-regex";
import { DEFAULT_GRID_RESOLUTION } from "../consts";
import { blockYouJustCreatedAtom } from "../jotai/atoms";
import { styled } from "../stitches.config";
import { AnyBlock, TextBlock as TextBlockType } from "../types";
import { remToPixels } from "../ui/Minimap";

let canvas = document.createElement("canvas");
let context = canvas.getContext("2d");

function getFontString(element: HTMLTextAreaElement) {
  let compStyles = window.getComputedStyle(element);
  let font =
    compStyles.getPropertyValue("font-size") +
    " " +
    compStyles.getPropertyValue("font-family");
  return font;
}
function getTextWidth(text: string, element: HTMLTextAreaElement) {
  // re-use canvas context for better performance
  if (!context) {
    console.error("context lost");
    context = canvas.getContext("2d");
    return 0;
  }
  context.font = getFontString(element);
  let metrics = context.measureText(text);
  return metrics.width;
}

export function useManagedTextArea(
  block: AnyBlock,
  updateBlock: (block: Partial<AnyBlock>) => void,
  deleteBlock: (blockId: string) => void
) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  let { value, size, id } = block;
  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    e.stopPropagation(); // prevent canvas key commands from conflicting
    switch (e.key) {
      case "Backspace":
        if (block.value === "") deleteBlock(id);
        break;
      case "Escape":
        textAreaRef!.current!.blur();
        break;
      default:
    }
  }

  function onChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    let delta = e.target.value.length - (value?.length || 0);
    let isBigPaste = delta > 100;
    updateBlock({
      value: e.target.value,
    });

    let textHeight = textAreaRef.current?.scrollHeight;
    if (!textHeight) return;

    let newSize = {
      ...size,
    };

    // horizontal expansion only occurs under certain circumstances:
    if (
      !e.target.value.includes("\n") &&
      size.height < 3 &&
      size.width < 12 && // one "post-it note's" width
      !isBigPaste
    ) {
      // "W" is the widest letter, ensuring there is space for the next letter.
      let unflowedTextWidth = getTextWidth(e.target.value + "W", e.target);
      newSize.width = Math.max(
        Math.ceil(
          (unflowedTextWidth + remToPixels(1)) / DEFAULT_GRID_RESOLUTION
        ),
        size.width
      );
    } else {
      // otherwise expand to fit vertically;
      newSize.height = Math.ceil(
        (textHeight + remToPixels(1)) / DEFAULT_GRID_RESOLUTION
      );
    }

    updateBlock({
      size: newSize,
    });
  }

  return { textAreaRef, onKeyDown, onChange };
}
function TextBlock({
  block,
  updateBlock,
  deleteBlock,
  justCreated,
}: {
  block: TextBlockType;
  justCreated: boolean;
  updateBlock: (block: Partial<AnyBlock>) => void;
  deleteBlock: (blockId: string) => void;
}) {
  const { textAreaRef, onKeyDown, onChange } = useManagedTextArea(
    block,
    updateBlock,
    deleteBlock
  );

  function onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    // For now only support pasting URL into blank box
    const pastedText = e.clipboardData.getData("text/plain");
    if (
      textAreaRef!.current!.value.trim() === "" &&
      pastedText.match(urlRegex({ exact: true }))
    ) {
      updateBlock({
        type: "link",
        value: pastedText,
      });
    }
    if (pastedText.trim().length > 0) {
      e.stopPropagation();
    }
  }

  let setBlockYouJustCreated = useUpdateAtom(blockYouJustCreatedAtom);
  useEffect(() => {
    if (justCreated && textAreaRef.current) {
      textAreaRef.current!.focus();
      setBlockYouJustCreated(undefined);
    }
  }, [justCreated, textAreaRef, setBlockYouJustCreated]);

  return (
    <TextArea
      onChange={onChange}
      onKeyDown={onKeyDown}
      rows={1} // acts as a "row minimum" (default is 2)
      spellCheck={false}
      onDoubleClick={() => {
        textAreaRef.current!.focus();
      }}
      style={{
        cursor:
          document.activeElement === textAreaRef.current ? "text" : "grab",
      }}
      onMouseDown={(e) => {
        // prevent moving/selecting block when it's focused
        if (document.activeElement === e.target) {
          e.stopPropagation();
        }
      }}
      value={block.value}
      ref={textAreaRef}
      onPaste={onPaste}
    ></TextArea>
  );
}

export default TextBlock;

const TextArea = styled("textarea", {
  margin: "$1",
  width: "calc(100% - $2)",
  height: "calc(100% - $2)",
  overflow: "hidden",
  padding: "$1",
  resize: "none",
  background: "transparent",
  color: "$brown",
  lineHeight: "1.5rem",
  fontSize: "$5",
});
